import { JSX, useCallback, useEffect, useRef, useState } from 'react';
import './style.scss';
import first from 'lodash/first';
import last from 'lodash/last';
import L from 'leaflet';
import Spinner from '../../../../../Shared/Spinner';
import CoordinateHelper from './helpers/coordinate.helper';
import type { ICoordinate } from '../../../../../../models/coordinate.model';
import type { IJourneyDetails } from '../../../../../../models/journey-details.model';
import type { IJourney } from '../../../../../../models/journey.model';
import JourneyEndpointMarker from './components/markers/JourneyEndpointMarker';
import DestinationLinkLabelMarker from './components/markers/DestinationLinkLabelMarker';
import { TranslationService } from '../../../../../../services/translation.service';
import { LabelType } from './components/models/label-type.enum';
import type { IServiceJourneyDetails } from '../../../../../../models/service-journey-details.model';
import TripLegLabelMarker from './components/markers/TripLegLabelMarker';
import ServiceJourneyPolylines from './components/layers/ServiceJourneyPolylines';
import CallMarker from './components/markers/CallMarker';
import NonPublicTransportEndpointMarker from './components/markers/NonPublicTransportEndpointMarker';
import type IBounds from '../../../../../Shared/Map/models/bounds.model';
import Map, { type IMap } from '../../../../../Shared/Map';
import type { IServiceJourney } from '../../../../../../models/service-journey.model';
import { AnalyticsService } from '../../../../../../services/analytics-service';
import NonPublicTransportPolyline from './components/layers/NonPublicTransportPolyline';
import { ISelectedNonPublicTransportLink } from '../../models/selected-non-public-transport';

function getStartCoordinate(journeyDetails: IJourneyDetails): ICoordinate | undefined {
  if (journeyDetails.departureAccessLink?.linkCoordinates) return first(journeyDetails.departureAccessLink?.linkCoordinates);
  return first(first(journeyDetails.tripLegs)?.tripLegCoordinates);
}

function departureAccessLinkConnectsToFirstTripLeg(journeyDetails: IJourneyDetails): boolean {
  const c1 = last(journeyDetails.departureAccessLink?.linkCoordinates);
  const c2 = first(first(journeyDetails.tripLegs)?.tripLegCoordinates);
  return CoordinateHelper.equals(c1, c2);
}

function arrivalAccessLinkConnectsToLastTripLeg(journeyDetails: IJourneyDetails): boolean {
  const c1 = last(last(journeyDetails.tripLegs)?.tripLegCoordinates);
  const c2 = first(journeyDetails.arrivalAccessLink?.linkCoordinates);
  return CoordinateHelper.equals(c1, c2);
}

function getStopCoordinate(journeyDetails: IJourneyDetails): ICoordinate | undefined {
  if (journeyDetails.arrivalAccessLink?.linkCoordinates) return last(journeyDetails.arrivalAccessLink.linkCoordinates);
  return last(last(journeyDetails.tripLegs)?.tripLegCoordinates);
}

function JourneyMap({
  journey,
  journeyDetails,
  selectedServiceJourney = undefined,
  selectedNonPublicTransportLink = undefined,
  isLoading,
}: {
  journey: IJourney;
  journeyDetails: IJourneyDetails;
  selectedServiceJourney?: IServiceJourney | undefined;
  selectedNonPublicTransportLink?: ISelectedNonPublicTransportLink | undefined;
  isLoading: boolean;
}) {
  // === STATE ===
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (typeof window === 'undefined' ? false : window.matchMedia('(max-width: 767.98px)').matches));

  // === REFS ===
  const mapRef = useRef<IMap>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767.98px)');

    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsMobileViewport(event.matches);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);

      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }

    mediaQuery.addListener(handleChange);

    return () => {
      mediaQuery.removeListener(handleChange);
    };
  }, []);

  function getMarkersAndLayers(): [JSX.Element[] | undefined, JSX.Element[] | undefined] {
    if (!journeyDetails) return [undefined, undefined];

    const markers: JSX.Element[] = [];
    const layers: JSX.Element[] = [];

    // Check if the journey is only a walk or bike (i.e. destination link) otherwise continue with a regular journey
    if (journeyDetails.destinationLink) {
      if (journeyDetails.destinationLink.linkCoordinates && journeyDetails.destinationLink.linkCoordinates.length > 0) {
        markers.push(<JourneyEndpointMarker coordinate={journeyDetails.destinationLink.linkCoordinates[0]} text={TranslationService.get('common.map.start')} key={`${journey.id}-start`} />);
      }

      markers.push(<DestinationLinkLabelMarker labelType={LabelType.departure} link={journeyDetails.destinationLink} key={`${journey.id}-destination`} />);

      if (journeyDetails.destinationLink.linkCoordinates) {
        layers.push(<NonPublicTransportPolyline coordinates={journeyDetails.destinationLink.linkCoordinates} key={`${journey.id}-non-public-transport-polyline`} />);
      }

      markers.push(<DestinationLinkLabelMarker labelType={LabelType.arrival} link={journeyDetails.destinationLink} key={`${journey.id}-arrival`} />);

      if (journeyDetails.destinationLink.linkCoordinates && journeyDetails.destinationLink.linkCoordinates.length > 0) {
        markers.push(
          <JourneyEndpointMarker
            coordinate={journeyDetails.destinationLink.linkCoordinates[journeyDetails.destinationLink.linkCoordinates.length - 1]}
            text={TranslationService.get('common.map.end')}
            key={`${journey.id}-end`}
          />,
        );
      }

      return [markers, layers];
    }

    if (!journeyDetails.tripLegs || journeyDetails.tripLegs.length === 0) return [undefined, undefined];

    // Add start marker (A)
    const startCoordinate = getStartCoordinate(journeyDetails);
    if (startCoordinate) {
      markers.push(<JourneyEndpointMarker coordinate={startCoordinate} text={TranslationService.get('common.map.start')} key={`${journey.id}-start`} />);
    }

    // Add service journey lines
    journeyDetails.tripLegs.forEach((tripLeg) => {
      // NV-3575: For some reason we sometimes get service journeys that aren't part of the trip leg, skip these
      const hasAnyCallOnTripLeg = (x: IServiceJourneyDetails) => x.callsOnServiceJourney?.some((c) => c.isOnTripLeg) ?? false;
      const serviceJourneysWithCalls = tripLeg.serviceJourneys?.filter(hasAnyCallOnTripLeg);

      if (serviceJourneysWithCalls && serviceJourneysWithCalls.length > 0) {
        const firstServiceJourney = serviceJourneysWithCalls[0];
        const lastServiceJourney = serviceJourneysWithCalls[serviceJourneysWithCalls.length - 1];
        const isDisabled = (!!selectedServiceJourney && serviceJourneysWithCalls.some((sj) => sj.gid !== selectedServiceJourney.gid)) || !!selectedNonPublicTransportLink;

        layers.push(
          <TripLegLabelMarker disabled={isDisabled} serviceJourney={firstServiceJourney} labelType={LabelType.departure} key={`${firstServiceJourney.gid}-trip-leg-label-marker-departure`} />,
        );

        serviceJourneysWithCalls.forEach((serviceJourney) => {
          layers.push(
            <ServiceJourneyPolylines
              serviceJourney={serviceJourney}
              disabled={(!!selectedServiceJourney && selectedServiceJourney.gid !== serviceJourney.gid) || !!selectedNonPublicTransportLink}
              key={`${serviceJourney.gid}-service-journey-polyline`}
            />,
          );
          serviceJourney.callsOnServiceJourney?.forEach((call) => {
            const callIsEnabled = selectedServiceJourney !== undefined ? selectedServiceJourney.gid === serviceJourney.gid && call.isOnTripLeg : call.isOnTripLeg;
            markers.push(<CallMarker call={call} line={serviceJourney.line} key={`${serviceJourney.gid}-${call.index}-call-marker`} iconOpacity={callIsEnabled ? 1 : 0.35} />);
          });
        });
        markers.push(<TripLegLabelMarker disabled={isDisabled} serviceJourney={lastServiceJourney} labelType={LabelType.arrival} key={`${lastServiceJourney.gid}-trip-leg-label-marker-arrival`} />);
      }
    });

    // Add connection links last to be on top of service journey lines
    journeyDetails.connectionLinks?.forEach((connectionLink) => {
      if (connectionLink.linkCoordinates) {
        layers.push(<NonPublicTransportPolyline coordinates={connectionLink.linkCoordinates} key={`${journey.id}-${connectionLink.journeyLegIndex}-tripleg-non-public-transport-polyline`} />);
      }
    });

    // Add access links
    if (journeyDetails.departureAccessLink?.linkCoordinates) {
      layers.push(<NonPublicTransportPolyline coordinates={journeyDetails.departureAccessLink.linkCoordinates} key={`${journey.id}-departureAccessLink-polyline`} />);
      if (journeyDetails.departureAccessLink.linkCoordinates && journeyDetails.departureAccessLink.linkCoordinates.length > 0) {
        markers.push(<NonPublicTransportEndpointMarker coordinate={journeyDetails.departureAccessLink.linkCoordinates[0]} key={`${journey.id}-departureAccessLink-start`} />);
      }
      if (!departureAccessLinkConnectsToFirstTripLeg(journeyDetails)) {
        const departureAccessLinkEndCoordinate = last(journeyDetails.departureAccessLink.linkCoordinates);
        const firstTripLegCoordinate = first(first(journeyDetails.tripLegs)?.tripLegCoordinates);
        if (departureAccessLinkEndCoordinate && firstTripLegCoordinate) {
          layers.push(<NonPublicTransportPolyline coordinates={[departureAccessLinkEndCoordinate, firstTripLegCoordinate]} key={`${journey.id}-departureAccessLink-first-last-polyline`} />);
          markers.push(<NonPublicTransportEndpointMarker coordinate={departureAccessLinkEndCoordinate} key={`${journey.id}-arrivalAccessLink-tripleg-end`} />);
        }
      }
    }

    if (journeyDetails.arrivalAccessLink?.linkCoordinates) {
      layers.push(<NonPublicTransportPolyline coordinates={journeyDetails.arrivalAccessLink.linkCoordinates} key={`${journey.id}-arrivalAccessLink-polyline`} />);
      const arrivalAccessLinkEndCoordinate = last(journeyDetails.arrivalAccessLink.linkCoordinates);
      if (arrivalAccessLinkEndCoordinate) {
        markers.push(<NonPublicTransportEndpointMarker coordinate={arrivalAccessLinkEndCoordinate} key={`${journey.id}-arrivalAccessLink-end`} />);
      }
      if (!arrivalAccessLinkConnectsToLastTripLeg(journeyDetails)) {
        const lastTripLegEndCoordinate = last(last(journeyDetails.tripLegs)?.tripLegCoordinates);
        const arrivalAccessLinkStartCoordinate = first(journeyDetails.arrivalAccessLink.linkCoordinates);
        if (lastTripLegEndCoordinate && arrivalAccessLinkStartCoordinate) {
          layers.push(<NonPublicTransportPolyline coordinates={[lastTripLegEndCoordinate, arrivalAccessLinkStartCoordinate]} key={`${journey.id}-arrivalAccessLink-last-polyline`} />);
          markers.push(<NonPublicTransportEndpointMarker coordinate={arrivalAccessLinkStartCoordinate} key={`${journey.id}-arrivalAccessLink-tripleg-end`} />);
        }
      }
    }

    // Add stop marker (B)
    const stopCoordinate = getStopCoordinate(journeyDetails);
    if (stopCoordinate) {
      markers.push(<JourneyEndpointMarker coordinate={stopCoordinate} text={TranslationService.get('common.map.end')} key={`${journey.id}-end`} />);
    }

    return [markers, layers];
  }

  const getBounds = useCallback<() => IBounds | undefined>(() => {
    if (!journeyDetails) return undefined;

    if (journeyDetails.destinationLink?.linkCoordinates) {
      return { bounds: new L.LatLngBounds(journeyDetails.destinationLink.linkCoordinates.map((c) => [c.latitude, c.longitude])), padding: 0.1, method: 'fitBounds' };
    }
    let allJourneyCoords: [number, number][] = [];
    if (!selectedNonPublicTransportLink) {
      journeyDetails.tripLegs?.forEach((tripLeg) => {
        if (selectedServiceJourney && tripLeg.serviceJourneys && !tripLeg.serviceJourneys.some((sj) => sj.gid === selectedServiceJourney.gid)) return;
        tripLeg.callsOnTripLeg?.forEach((call) => {
          if (call.latitude && call.longitude) {
            allJourneyCoords.push([call.latitude, call.longitude]);
          }
        });
      });
    }
    if (selectedNonPublicTransportLink) {
      if (selectedNonPublicTransportLink.arrivalAccessLink) allJourneyCoords = allJourneyCoords.concat(journeyDetails.arrivalAccessLink?.linkCoordinates?.map((c) => [c.latitude, c.longitude]) ?? []);
      if (selectedNonPublicTransportLink.departureAccessLink)
        allJourneyCoords = allJourneyCoords.concat(journeyDetails.departureAccessLink?.linkCoordinates?.map((c) => [c.latitude, c.longitude]) ?? []);
      if (selectedNonPublicTransportLink.connectionLinkLegIndex !== undefined) {
        const connectionLink = journeyDetails.connectionLinks?.find((cl) => cl.journeyLegIndex === selectedNonPublicTransportLink.connectionLinkLegIndex);
        if (connectionLink?.linkCoordinates) {
          allJourneyCoords = allJourneyCoords.concat(connectionLink.linkCoordinates.map((c) => [c.latitude, c.longitude]));
        }
      }
    }
    return { bounds: new L.LatLngBounds(allJourneyCoords), padding: 0.1, method: 'fitBounds' } as IBounds;
  }, [journeyDetails, selectedServiceJourney, selectedNonPublicTransportLink]);

  useEffect(() => {
    setTimeout(() => {
      mapRef.current?.invalidateSize();
      const bounds = getBounds();
      if (bounds) {
        mapRef.current?.fitBounds(bounds);
      }
    }, 200);
  }, [getBounds]);

  const [markers, layers] = getMarkersAndLayers();
  const hasSelectedJourney = selectedServiceJourney !== undefined || selectedNonPublicTransportLink !== undefined;
  const showMobileMap = isMobileExpanded || hasSelectedJourney;
  const mapId = `journey-details-map-${journey.id}`;

  // Prevent tabbing into the map when it's not shown on mobile, by setting the inert attribute on the map container. This ensures that screen readers also ignore the map when it's hidden.
  useEffect(() => {
    const mapContainer = document.getElementById(mapId);

    if (!mapContainer) return;

    const shouldPreventFocus = isMobileViewport && !showMobileMap;

    if (shouldPreventFocus) {
      mapContainer.setAttribute('inert', '');
      mapContainer.setAttribute('aria-hidden', 'true');
      return;
    }

    mapContainer.removeAttribute('inert');
    mapContainer.removeAttribute('aria-hidden');
  }, [isMobileViewport, showMobileMap, mapId]);

  return (
    <div className={`journey-details-map ${showMobileMap ? 'journey-details-map--mobile-expanded' : ''} position-relative rounded overflow-hidden`}>
      {isLoading && <Spinner />}
      {journeyDetails && (
        <Map
          mapId={mapId}
          ref={mapRef}
          key={mapId}
          ariaLabel={TranslationService.get('common.map.map')}
          markers={markers}
          layers={layers}
          bounds={!hasSelectedJourney ? getBounds() : undefined}
          className="travel-planner-result-map__map"
          extraMarkers={
            !hasSelectedJourney && isMobileExpanded
              ? [
                  <button
                    key="toggle-button"
                    type="button"
                    className={`btn py-1 journey-details-map__toggle-map-button fw-normal journey-details-map__toggle-map-button--mobile-expanded position-absolute bg-white rounded d-md-none`}
                    aria-controls={mapId}
                    aria-expanded={showMobileMap}
                    onClick={() => {
                      setIsMobileExpanded(false);
                      AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Göm karta, mobil', '');
                    }}
                  >
                    {TranslationService.get('common.map.hidemap')}
                  </button>,
                ]
              : []
          }
        />
      )}
      <div className={`${showMobileMap ? 'd-none' : 'd-flex'} justify-content-center align-items-center w-100 h-100 d-md-none journey-details-map__background position-absolute`}>
        <div className="w-100 h-100 journey-details-map__backdrop position-absolute"></div>
        {(!hasSelectedJourney || !isMobileExpanded) && (
          <button
            key="toggle-button"
            type="button"
            className={`btn py-1 journey-details-map__toggle-map-button fw-normal bg-white rounded`}
            aria-controls={mapId}
            aria-expanded={showMobileMap}
            onClick={() => {
              setIsMobileExpanded(true);
              setTimeout(() => {
                mapRef.current?.invalidateSize();
                mapRef.current?.fitBounds(getBounds());
              }, 400);
              AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Visa karta, mobil', '');
            }}
          >
            {TranslationService.get('common.map.showmap')}
          </button>
        )}
      </div>
    </div>
  );
}

export default JourneyMap;
